import { type ReactNode, useEffect } from 'react';
import { Box, InlineNotification, Typography, useDeviceType, Stack } from '@packages/shared';
import { type Messages } from '@empiriecom/mybuy-frontend-api/backend-mybuy-checkout/v1/models/Messages';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import { Page } from '@empiriecom/mybuy-frontend-api/backend-mybuy-checkout/v1/models/Page';
import { useIntl } from 'react-intl';
import type { SxProps, Theme } from '@mui/material';
import { ScrollTopButton } from '@packages/modules';
import { ProgressBar } from '../ProgressBar';

type CheckoutContainerProps = {
  children?: ReactNode;
  step: number;
  messages?: Messages;
  errorMessage?: string;
  headline?: ReactNode;
  subline?: ReactNode;
  stepSubline?: ReactNode;
  testId?: string;
  rightContent?: ReactNode;
  saveAndNavigate?: (targetPage: Page) => Promise<any>;
};

const stepSublineTypographyStyling: SxProps<Theme> = {
  fontSize: '0.812rem',
  lineHeight: '1.125rem',
  letterSpacing: '0.2px',
  color: (theme: Theme) => theme.palette.text.darkTransparent,
};
const headlineContainer: SxProps<Theme> = {
  textAlign: {
    xs: 'center',
    md: 'left',
  },
  m: {
    xs: '0 auto',
    md: '1.063rem 1.25rem 0.938rem',
  },
};
const checkoutRootBoxStyling: SxProps<Theme> = {
  maxWidth: (theme: Theme) => theme.breakpoints.values.xl,
  minWidth: 0,
  width: '100%',
  m: {
    xs: '',
    md: '0 auto',
  },
};
const InlineNotificationStyling: SxProps<Theme> = {
  m: '1rem 0',
};
const headlineTypographyStyling: SxProps<Theme> = {
  fontSize: { md: '1.625rem' },
  mt: {
    xs: 1.5,
    md: '0',
  },
};
const childrenBoxStyling: SxProps<Theme> = {
  mt: { sm: '0.875rem', md: '0' },
  maxWidth: '49.563rem',
  width: '100%',
  flexGrow: 1,
};

export const CheckoutContainer: React.FC<CheckoutContainerProps> = ({
  children,
  step,
  messages,
  errorMessage,
  headline,
  subline,
  stepSubline,
  // istanbul ignore next
  testId = 'checkout-root',
  rightContent,
  saveAndNavigate,
}) => {
  const { isDesktop } = useDeviceType();
  const { formatMessage } = useIntl();

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <Box sx={checkoutRootBoxStyling} data-testid={testId}>
      <ProgressBar activeStep={step} saveAndNavigate={saveAndNavigate} />
      {errorMessage && (
        <InlineNotification severity="error" sx={InlineNotificationStyling}>
          {errorMessage}
        </InlineNotification>
      )}
      {messages &&
        messages.elements.map((message) => (
          <InlineNotification
            key={message.description}
            severity={message.level}
            sx={InlineNotificationStyling}
          >
            {formatMessage(
              { id: 'backend-error', defaultMessage: `${message.description}` },
              {
                break: <br />,
              },
            )}
          </InlineNotification>
        ))}
      <Box sx={headlineContainer}>
        <Typography variant="h3" component="div" sx={headlineTypographyStyling}>
          {headline}
        </Typography>
        <Box>
          {subline}
          {!isDesktop && stepSubline && (
            <Typography component="p" sx={stepSublineTypographyStyling}>
              {stepSubline}
            </Typography>
          )}
        </Box>
      </Box>
      <Stack direction="row" justifyContent="space-between">
        <Box sx={childrenBoxStyling}>{children}</Box>
        {isDesktop && (
          <Stack direction="column" maxWidth="20.75rem" ml={2} gap={2}>
            {rightContent}
          </Stack>
        )}
      </Stack>
      <Box
        id="scrollup-wrapper"
        sx={{
          position: 'fixed',
          bottom: '2%',
          right: '2%',
          zIndex: 'appBar',
        }}
      >
        <ScrollTopButton />
      </Box>
    </Box>
  );
};
